import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { setSidebarOpen } from '../../store/slices/ui.slice';
import { useWhiteLabel } from '../../contexts/WhiteLabelContext';

export default function Sidebar() {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const location = useLocation();
  const sidebarOpen = useSelector((state: RootState) => state.ui.sidebarOpen);
  const user = useSelector((state: RootState) => state.auth.user);
  const isAdmin = user?.isSuperAdmin === true;
  const { config: whiteLabelConfig } = useWhiteLabel();

  // State para sidebar collapsed
  const [isCollapsed, setIsCollapsed] = useState(false);

  // State para grupos expandidos
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    analyticsMobile: false,
    analyticsWeb: false,
    apm: false,
    infrastructure: false,
    status: false,
    scanners: false,
    ml: false,
    admin: false,
  });

  // Auto-expand menu group based on current route
  useEffect(() => {
    const pathname = location.pathname;

    // Map routes to their parent groups
    if (pathname.startsWith('/analytics/mobile')) {
      setExpandedGroups(prev => ({ ...prev, analyticsMobile: true }));
    } else if (pathname.startsWith('/analytics') || pathname.startsWith('/rum') || pathname.startsWith('/errors')) {
      setExpandedGroups(prev => ({ ...prev, analyticsWeb: true }));
    } else if (pathname.startsWith('/apm')) {
      setExpandedGroups(prev => ({ ...prev, apm: true }));
    } else if (pathname.startsWith('/infrastructure') || pathname.startsWith('/metrics')) {
      setExpandedGroups(prev => ({ ...prev, infrastructure: true }));
    } else if (pathname.startsWith('/ml')) {
      setExpandedGroups(prev => ({ ...prev, ml: true }));
    } else if (pathname.startsWith('/status-pages') || pathname.startsWith('/uptime')) {
      setExpandedGroups(prev => ({ ...prev, status: true }));
    } else if (pathname.startsWith('/cloud-scan') || pathname.startsWith('/security') || pathname.startsWith('/seo-scanner')) {
      setExpandedGroups(prev => ({ ...prev, scanners: true }));
    } else if (pathname.startsWith('/admin')) {
      setExpandedGroups(prev => ({ ...prev, admin: true }));
    }
  }, [location.pathname]);

  const toggleGroup = (group: string) => {
    if (!isCollapsed) {
      setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
    }
  };

  const closeSidebarOnMobile = () => {
    if (window.innerWidth < 1024) {
      dispatch(setSidebarOpen(false));
    }
  };

  // Ícones (reutilizados)
  const icons = {
    dashboard: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    mobile: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    web: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
    agents: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
      </svg>
    ),
    apm: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    ),
    logs: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    ),
    infrastructure: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
      </svg>
    ),
    scanners: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    status: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    alerts: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    ml: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
    affiliate: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    admin: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
      </svg>
    ),
    settings: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  };

  // Itens de navegação agrupados por PLATAFORMA (Mobile / Web / Servidores...)
  const navSections = [
    {
      id: 'overview',
      label: t('nav.sectionOverview', 'Visão Geral'),
      items: [
        { name: t('nav.dashboard'), href: '/dashboard', tourId: 'nav-dashboard', icon: icons.dashboard },
      ],
    },
    {
      id: 'mobile',
      label: t('nav.sectionMobile', 'Mobile'),
      items: [
        {
          name: t('nav.analyticsMobile', 'Analytics Mobile'),
          href: '/analytics/mobile/journeys',
          group: 'analyticsMobile',
          tourId: 'nav-analytics-mobile',
          icon: icons.mobile,
          children: [
            { name: t('nav.mobileJourneys'), href: '/analytics/mobile/journeys' },
            { name: t('nav.mobileSetup'), href: '/analytics/mobile/setup' },
          ],
        },
      ],
    },
    {
      id: 'web',
      label: t('nav.sectionWeb', 'Web / Site'),
      items: [
        {
          name: t('nav.analyticsWeb', 'Analytics Web'),
          href: '/analytics/overview',
          group: 'analyticsWeb',
          tourId: 'nav-analytics',
          icon: icons.web,
          children: [
            { name: t('nav.overview'), href: '/analytics/overview' },
            { name: 'Real User Monitoring (RUM)', href: '/analytics/rum' },
            { name: t('nav.analyticsEvents'), href: '/analytics/events' },
            { name: t('nav.analyticsFunnels'), href: '/analytics/funnels' },
            { name: t('nav.analyticsRetention'), href: '/analytics/retention' },
            { name: t('nav.analyticsGeography'), href: '/analytics/geography' },
            { name: t('nav.analyticsDevices'), href: '/analytics/devices' },
            { name: t('nav.analyticsPages'), href: '/analytics/pages' },
            { name: t('nav.analyticsTraffic'), href: '/analytics/traffic' },
            { name: t('nav.analyticsJourneys'), href: '/analytics/journeys', badge: '🔥' },
            { name: t('nav.sdkSetup'), href: '/analytics/sdk-setup' },
          ],
        },
        {
          name: t('nav.scanners'),
          href: '/cloud-scan',
          group: 'scanners',
          tourId: 'nav-scanners',
          icon: icons.scanners,
          badge: 'NEW',
          badgeColor: 'from-emerald-500 to-teal-600',
          children: [
            { name: t('nav.cloudScan'), href: '/cloud-scan' },
            { name: t('nav.security'), href: '/security' },
            { name: t('nav.seoScanner', 'SEO Scanner'), href: '/seo-scanner' },
          ],
        },
      ],
    },
    {
      id: 'infra',
      label: t('nav.sectionInfra', 'Servidores & Infra'),
      items: [
        {
          name: t('nav.apm'),
          href: '/apm',
          group: 'apm',
          tourId: 'nav-apm',
          icon: icons.apm,
          children: [
            { name: t('nav.serviceMap', 'Service Map'), href: '/apm/service-map' },
            { name: t('nav.traces', 'Traces'), href: '/apm/traces' },
            { name: t('nav.transactions', 'Transactions'), href: '/apm/transactions' },
            { name: t('nav.errors', 'Errors'), href: '/apm/errors' },
            { name: t('nav.database'), href: '/apm/database' },
          ],
        },
        { name: t('nav.logs'), href: '/logs', tourId: 'nav-logs', icon: icons.logs },
        {
          name: t('nav.infrastructure'),
          href: '/infrastructure',
          group: 'infrastructure',
          tourId: 'nav-infrastructure',
          icon: icons.infrastructure,
          children: [
            { name: t('nav.hosts', 'Hosts'), href: '/infrastructure/hosts' },
            { name: t('nav.containers', 'Containers'), href: '/infrastructure/containers' },
            { name: t('nav.kubernetes', 'Kubernetes'), href: '/infrastructure/kubernetes' },
            { name: t('nav.logCollection', 'Firewall Logs'), href: '/infrastructure/log-collection' },
            { name: t('nav.metrics', 'Metrics'), href: '/metrics' },
          ],
        },
        { name: t('nav.agents'), href: '/agents', tourId: 'nav-agents', icon: icons.agents },
        {
          name: t('nav.statusUptime'),
          href: '/status-pages',
          group: 'status',
          tourId: 'nav-status',
          icon: icons.status,
          children: [
            { name: t('nav.statusPages'), href: '/status-pages' },
            { name: t('nav.uptime'), href: '/uptime' },
          ],
        },
      ],
    },
    {
      id: 'intelligence',
      label: t('nav.sectionIntelligence', 'Inteligência'),
      items: [
        { name: t('nav.alerts', 'Alerts'), href: '/alerts', group: 'alerts', tourId: 'nav-alerts', icon: icons.alerts },
        {
          name: t('nav.ml'),
          href: '/ml',
          group: 'ml',
          tourId: 'nav-ml',
          icon: icons.ml,
          badge: 'AI',
          badgeColor: 'from-pink-500 to-rose-600',
          children: [
            { name: t('nav.anomalies', 'Anomalies'), href: '/ml/anomalies' },
            { name: t('nav.forecasts', 'Forecasts'), href: '/ml/forecasts' },
            { name: t('nav.insights', 'Insights'), href: '/ml/insights' },
          ],
        },
      ],
    },
    {
      id: 'account',
      label: t('nav.sectionAccount', 'Conta'),
      items: [
        {
          name: t('nav.affiliate'),
          href: '/affiliate/dashboard',
          tourId: 'nav-affiliate',
          icon: icons.affiliate,
          badge: 'EARN',
          badgeColor: 'from-amber-500 to-orange-600',
        },
        ...(isAdmin
          ? [
              {
                name: t('nav.admin'),
                href: '/admin/plans',
                group: 'admin',
                icon: icons.admin,
                badge: 'ADMIN',
                badgeColor: 'from-red-500 to-rose-600',
                children: [
                  { name: t('nav.adminPlans'), href: '/admin/plans' },
                  { name: t('nav.adminCustomers'), href: '/admin/tenants' },
                  { name: t('nav.adminUsers'), href: '/admin/users' },
                  { name: t('nav.adminSubscriptions'), href: '/admin/subscriptions' },
                  { name: t('nav.adminInvoices'), href: '/admin/invoices' },
                  { name: t('nav.adminAffiliates'), href: '/admin/affiliates' },
                  { name: t('nav.adminCloudScans'), href: '/admin/cloud-scans' },
                ],
              },
            ]
          : []),
        { name: t('nav.settings'), href: '/settings', tourId: 'nav-settings', icon: icons.settings },
      ],
    },
  ];

  const renderItem = (item: any) => {
    const isGroupExpanded = item.group ? expandedGroups[item.group] : false;
    // Check if any child route is active
    const isChildActive = item.children?.some((child: any) =>
      location.pathname === child.href || location.pathname.startsWith(child.href + '/')
    ) || false;

    return (
      <div key={item.name} className="relative group">
        <NavLink
          to={item.href}
          data-tour={item.tourId}
          onClick={(e) => {
            if (item.children && !isCollapsed) {
              e.preventDefault();
              toggleGroup(item.group!);
            } else {
              closeSidebarOnMobile();
            }
          }}
          className={({ isActive }) =>
            `flex items-center justify-between rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
              isActive || isChildActive
                ? 'bg-gradient-to-r from-indigo-500/10 via-purple-500/10 to-pink-500/10 text-indigo-600 dark:text-indigo-400 shadow-lg shadow-indigo-500/10 dark:shadow-indigo-500/5 border border-indigo-200/50 dark:border-indigo-800/50'
                : 'text-gray-700 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-50 dark:text-gray-300 dark:hover:from-gray-800 dark:hover:to-gray-800/50 hover:shadow-md'
            } ${isCollapsed ? 'justify-center' : ''}`
          }
        >
          <div className="flex items-center space-x-3 min-w-0">
            <div className="flex-shrink-0 transform group-hover:scale-110 transition-transform duration-200">
              {item.icon}
            </div>
            {!isCollapsed && (
              <span className="truncate">{item.name}</span>
            )}
          </div>

          {!isCollapsed && (
            <div className="flex items-center space-x-2 flex-shrink-0">
              {item.badge && (
                <span className={`inline-flex items-center rounded-full bg-gradient-to-r ${item.badgeColor || 'from-indigo-500 to-purple-600'} px-2 py-0.5 text-xs font-bold text-white shadow-lg animate-pulse-slow`}>
                  {item.badge}
                </span>
              )}
              {item.children && (
                <svg
                  className={`w-4 h-4 transition-transform duration-300 ${isGroupExpanded ? 'rotate-90' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </div>
          )}
        </NavLink>

        {/* Tooltip for collapsed sidebar - Desktop only */}
        {isCollapsed && (
          <div className="hidden lg:block absolute left-full ml-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 z-50 pointer-events-none transition-opacity duration-200">
            <div className="bg-gray-900 dark:bg-gray-800 text-white text-sm px-3 py-2 rounded-lg shadow-xl whitespace-nowrap">
              {item.name}
              {item.badge && (
                <span className={`ml-2 inline-flex items-center rounded-full bg-gradient-to-r ${item.badgeColor || 'from-indigo-500 to-purple-600'} px-2 py-0.5 text-xs font-bold text-white`}>
                  {item.badge}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Submenu */}
        {item.children && !isCollapsed && (
          <div
            className={`ml-11 mt-1 space-y-1 overflow-hidden transition-all duration-300 ${
              isGroupExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            {item.children.map((child: any) => (
              <NavLink
                key={child.name}
                to={child.href}
                onClick={closeSidebarOnMobile}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-lg px-3 py-2 text-sm transition-all duration-200 ${
                    isActive
                      ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-50/50 dark:bg-indigo-900/20 font-medium'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100/50 dark:hover:bg-gray-800/50'
                  } ${'badge' in child && child.badge ? 'font-semibold' : ''}`
                }
              >
                <span className="truncate">{child.name}</span>
                {'badge' in child && child.badge && (
                  <span className="ml-1 flex-shrink-0 text-base leading-none">{child.badge}</span>
                )}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity duration-300"
          onClick={() => dispatch(setSidebarOpen(false))}
        />
      )}

      {/* Sidebar */}
      <aside
        data-tour="sidebar"
        className={`fixed left-0 top-0 z-40 flex h-screen flex-col transform border-r border-gray-200/80 bg-white/95 backdrop-blur-xl transition-all duration-300 ease-in-out dark:border-gray-800/80 dark:bg-gray-900/95 shadow-2xl ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 w-64 ${isCollapsed ? 'lg:w-20' : 'lg:w-64'}`}
      >
        {/* Logo & Toggle */}
        <div className="relative flex h-16 flex-shrink-0 items-center justify-between border-b border-gray-200/80 px-3 dark:border-gray-800/80 bg-gradient-to-r from-indigo-50/50 to-purple-50/50 dark:from-gray-800/50 dark:to-gray-900/50">
          {/* Close button - Visible only on mobile */}
          <button
            onClick={() => dispatch(setSidebarOpen(false))}
            className="lg:hidden absolute top-3 right-3 flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors duration-200 z-10"
            title="Close sidebar"
          >
            <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {isCollapsed ? (
            /* Logo centered quando collapsed - Desktop only */
            <div className="hidden lg:flex items-center justify-center w-full">
              <img
                src={whiteLabelConfig.logo_url || "/images/kubmix_icon.png"}
                alt={`${whiteLabelConfig.custom_name || 'Kubmix'} Logo`}
                className="h-10 w-10 object-contain flex-shrink-0 transform hover:scale-110 transition-all duration-200"
              />
            </div>
          ) : null}

          {/* Logo + Nome - Always visible on mobile, conditional on desktop */}
          <div className={`flex items-center space-x-3 min-w-0 flex-1 ${isCollapsed ? 'lg:hidden' : ''}`}>
            <img
              src={whiteLabelConfig.logo_url || "/images/kubmix_icon.png"}
              alt={`${whiteLabelConfig.custom_name || 'Kubmix'} Logo`}
              className="h-10 w-10 object-contain flex-shrink-0 transform hover:scale-110 transition-all duration-200"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 bg-clip-text text-transparent whitespace-nowrap animate-fade-in">
              {whiteLabelConfig.custom_name || 'Kubmix'}
            </span>
          </div>

          {/* Toggle Button - Hidden on mobile, conditional on desktop */}
          {!isCollapsed && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:flex items-center justify-center w-8 h-8 flex-shrink-0 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors duration-200 group"
              title="Collapse"
            >
              <svg
                className="w-5 h-5 text-gray-600 dark:text-gray-400 transition-transform duration-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}

          {/* Toggle Button quando collapsed - Hidden on mobile */}
          {isCollapsed && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:flex absolute top-3 right-2 items-center justify-center w-6 h-6 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors duration-200 group z-10"
              title="Expand"
            >
              <svg
                className="w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform duration-300 rotate-180"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent hover:scrollbar-thumb-gray-400 dark:scrollbar-thumb-gray-700 dark:hover:scrollbar-thumb-gray-600">
          {navSections.map((section, idx) => (
            <div key={section.id} className={idx > 0 ? 'pt-3' : ''}>
              {/* Cabeçalho da seção (ou divisória quando colapsado) */}
              {isCollapsed ? (
                idx > 0 && <div className="mx-2 mb-1 border-t border-gray-200/60 dark:border-gray-800/60" />
              ) : (
                <div className="px-3 pb-1">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    {section.label}
                  </span>
                </div>
              )}
              <div className="space-y-1">
                {section.items.map((item) => renderItem(item))}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer — minimal: status + versão */}
        <div className="flex-shrink-0 border-t border-gray-200/70 px-4 py-3 dark:border-gray-800/70">
          {!isCollapsed ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-60"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
                </span>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Online</span>
              </div>
              <span className="text-xs text-gray-400 dark:text-gray-600">v1.0.0</span>
            </div>
          ) : (
            <div className="flex justify-center">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-60"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500"></span>
              </span>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
